import errorReporter from '@app/errorReporter';
import { Color } from '@bb/elements-web';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type GetFolderHierarchyResponse,
  getFolderHierarchy,
} from 'api/AssetController';
import { Breadcrumbs, Card } from 'components';
import React, { useMemo } from 'react';
import styled from 'styled-components';

type AssetsPageHeaderProps = {
  folderId?: string;
  onRootFolderIdUpdate: (folderId: string | null) => void;
};

const AssetsPageHeader: React.FC<AssetsPageHeaderProps> = ({
  folderId,
  onRootFolderIdUpdate,
}) => {
  const queryClient = useQueryClient();

  const { data: folderHierarchy = [] } = useQuery({
    queryKey: ['folderHierarchy', folderId],
    queryFn: () => {
      // Get the existing data from the previous query
      const existingData = queryClient.getQueryData([
        'folderHierarchy',
      ]) satisfies GetFolderHierarchyResponse;

      // return a subset of existing data if possible
      if (existingData?.length > 0) {
        const folderIndex = existingData.findIndex(
          folder => folder.id === folderId,
        );
        if (folderIndex !== -1) {
          return existingData.slice(
            0,
            folderIndex + 1,
          ) satisfies GetFolderHierarchyResponse;
        }
      }
      return getFolderHierarchy({ folderId: folderId! });
    },
    onSuccess: data => {
      if (data.length > 0) {
        onRootFolderIdUpdate(data[0].id);
      }
    },
    onError: fetchError => {
      errorReporter.error('Failed to load folder hierarchy:', fetchError);
    },
    enabled: !!folderId,
    keepPreviousData: !!folderId,
  });

  const breadcrumbRoutes = useMemo(() => {
    const routes = [{ breadcrumbName: 'BrokerBay Files', path: '/assets' }];
    if (!folderId) return routes;

    folderHierarchy.forEach(folder => {
      routes.push({
        breadcrumbName: folder.name,
        path: `/assets/?folderId=${folder.id}`,
      });
    });
    return routes;
  }, [folderHierarchy, folderId]);

  return (
    <StyledCard>
      <Breadcrumbs separator=">" breadcrumbRoutes={breadcrumbRoutes} />
    </StyledCard>
  );
};

export default AssetsPageHeader;

const StyledCard = styled(Card)`
  && {
    margin-bottom: 5px;
  }

  && .ant-card-body {
    padding: 10px;
  }

  && .ant-breadcrumb {
    padding-top: 15px;
    padding-bottom: 5px;
  }

  && .ant-breadcrumb .ant-breadcrumb-link a,
  && .ant-breadcrumb .ant-breadcrumb-separator {
    font-size: 18px;
    color: ${Color.PLACEHOLDER};
    font-weight: 600;
  }
`;
